/**
 * used by observations:
 * - loadActive
 * - isGeneric
 * - draftCommitmentId
 * - commitmentId
 * - focus
 *
 * used by commitments:
 * - focus
 * - observationId
 * - metricList
 * - edit
 *
 * used by commitments via notifications:
 * - reason=goal
 * - goalId
 *
 * potentially used by both:
 * - metricId
 * - userId
 * - backOnCancel
 * - nt
 *
 * used by transcripts (pillar 2)
 * - tab
 * - transcriptId
 */

import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

const baseCmd = 'coaching-detail';

// eslint-disable-next-line @typescript-eslint/promise-function-async
export function navigateToNewMetricId(
    router: Router,
    activatedRoute: ActivatedRoute,
    metricId: number,
    nonKpiId: number,
    didFirstLoad: boolean,
): Promise<boolean> {
    return router.navigate([baseCmd, matchUserId(router.url)[1]], {
        queryParams: {
            metricId,
            nonKpiId,
            cmtIds: didFirstLoad
                ? undefined
                : activatedRoute.snapshot.queryParams.cmtIds,
            isNonKpi: didFirstLoad
                ? undefined
                : activatedRoute.snapshot.queryParams.isNonKpi,
        },
        queryParamsHandling: 'merge',
        // do a replace for nonKpiId changes since there's no real effect in the differences
        replaceUrl:
            metricId == null &&
            activatedRoute.snapshot.queryParams.metricId == null &&
            nonKpiId != null &&
            activatedRoute.snapshot.queryParams.nonKpiId != null,
    });
}

// eslint-disable-next-line @typescript-eslint/promise-function-async
export function navigateToNewUser(
    router: Router,
    userId: number,
    params?: {
        coachingCommand?: string;
    },
): Promise<boolean> {
    return router.navigate([baseCmd, userId], {
        queryParams: {
            coachingCommand: params?.coachingCommand,
        },
    });
}

/** @deprecated */
// eslint-disable-next-line @typescript-eslint/promise-function-async
export function navigateToNewObservation(
    router: Router,
    activatedRoute: ActivatedRoute,
    generic: boolean,
    draftCommitmentId: string,
    commitmentId: number,
    focus: string,
): Promise<boolean> {
    return router.navigate(
        [baseCmd, matchUserId(router.url)[1], 'observation'],
        {
            queryParams: {
                // eslint-disable-next-line @typescript-eslint/dot-notation
                metricId: activatedRoute.snapshot.queryParams['metricId'],
                loadActive: true,
                isGeneric: generic,
                draftCommitmentId,
                commitmentId,
                focus,
                observationId: undefined,
                metricList: undefined,
                reason: undefined,
                goalId: undefined,
                edit: undefined,
                cmtIds: undefined,
                isNonKpi: undefined,
                tab: undefined,
                transcriptId: undefined,
            },
            queryParamsHandling: 'merge',
        },
    );
}

// eslint-disable-next-line @typescript-eslint/promise-function-async
export function navigateToExistingObservation(
    router: Router,
    activatedRoute: ActivatedRoute,
    observationId: number,
): Promise<boolean> {
    return router.navigate(
        [baseCmd, matchUserId(router.url)[1], 'observation', observationId],
        {
            queryParams: {
                // eslint-disable-next-line @typescript-eslint/dot-notation
                metricId: activatedRoute.snapshot.queryParams['metricId'],
                loadActive: true,
                isGeneric: undefined,
                draftCommitmentId: undefined,
                commitmentId: undefined,
                focus: undefined,
                observationId: undefined,
                metricList: undefined,
                reason: undefined,
                goalId: undefined,
                edit: undefined,
                cmtIds: undefined,
                isNonKpi: undefined,
                tab: undefined,
                transcriptId: undefined,
            },
            queryParamsHandling: 'merge',
        },
    );
}

// eslint-disable-next-line @typescript-eslint/promise-function-async
export function navigateToNewDraftCommitment(
    router: Router,
    activatedRoute: ActivatedRoute,
    generic: boolean,
    focus: string,
    observationId,
    metricList: string,
    userId?: number,
): Promise<boolean> {
    const finalUserId = userId ?? matchUserId(router.url)[1];
    return router.navigate([baseCmd, finalUserId, 'draftcommitments'], {
        queryParams: {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            metricId: activatedRoute.snapshot.queryParams['metricId'],
            loadActive: undefined,
            isGeneric: generic,
            draftCommitmentId: undefined,
            commitmentId: undefined,
            focus,
            observationId,
            metricList,
            reason: undefined,
            goalId: undefined,
            edit: undefined,
            cmtIds: undefined,
            isNonKpi: undefined,
            tab: undefined,
            transcriptId: undefined,
        },
        queryParamsHandling: 'merge',
    });
}

/**
 * @deprecated
 * @param focus FocusIdentifier
 */
// eslint-disable-next-line @typescript-eslint/promise-function-async
export function navigateToExistingCommitment(
    router: Router,
    activatedRoute: ActivatedRoute,
    commitmentId: number,
    focus: string,
    observationId,
    metricList: string,
    edit: boolean,
    userId?: number,
): Promise<boolean> {
    const finalUserId = userId ?? matchUserId(router.url)[1];
    return router.navigate(
        [baseCmd, finalUserId, 'commitments', commitmentId],
        {
            queryParams: {
                // eslint-disable-next-line @typescript-eslint/dot-notation
                metricId: activatedRoute.snapshot.queryParams['metricId'],
                loadActive: undefined,
                isGeneric: undefined,
                draftCommitmentId: undefined,
                commitmentId: undefined,
                focus,
                observationId,
                metricList,
                reason: undefined,
                goalId: undefined,
                edit,
                cmtIds: undefined,
                isNonKpi: undefined,
                tab: undefined,
                transcriptId: undefined,
            },
            queryParamsHandling: 'merge',
        },
    );
}

/** @deprecated */
// eslint-disable-next-line @typescript-eslint/promise-function-async
export function navigateToExistingDraftCommitment(
    router: Router,
    activatedRoute: ActivatedRoute,
    draftId: string,
    approvalId: number,
    commitmentId: number,
    focus: string,
    observationId,
    metricList: string,
    edit: boolean,
    userId?: number,
): Promise<boolean> {
    const finalUserId = userId ?? matchUserId(router.url)[1];
    const cmds =
        commitmentId != null
            ? [
                  baseCmd,
                  finalUserId,
                  'commitments',
                  commitmentId,
                  'drafts',
                  draftId,
              ]
            : [baseCmd, finalUserId, 'draftcommitments', draftId];
    if (approvalId != null) {
        cmds.push('approvals', approvalId);
    }
    return router.navigate(cmds, {
        queryParams: {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            metricId: activatedRoute.snapshot.queryParams['metricId'],
            loadActive: undefined,
            draftCommitmentId: undefined,
            commitmentId: undefined,
            focus,
            observationId,
            metricList,
            reason: undefined,
            goalId: undefined,
            edit,
            cmtIds: undefined,
            isNonKpi: undefined,
            tab: undefined,
            transcriptId: undefined,
        },
        queryParamsHandling: 'merge',
    });
}

// eslint-disable-next-line @typescript-eslint/promise-function-async
export function navigateToDetail(
    router: Router,
    activatedRoute: ActivatedRoute,
    isAfterDeletion: boolean,
): Promise<boolean> {
    return router.navigate([baseCmd, matchUserId(router.url)[1]], {
        queryParams: {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            metricId: activatedRoute.snapshot.queryParams['metricId'],
            loadActive: undefined,
            isGeneric: undefined,
            draftCommitmentId: undefined,
            commitmentId: undefined,
            focus: undefined,
            observationId: undefined,
            metricList: undefined,
            reason: undefined,
            goalId: undefined,
            edit: undefined,
            tab: undefined,
            transcriptId: undefined,
        },
        queryParamsHandling: 'merge',
        replaceUrl: isAfterDeletion,
    });
}

export function replaceStateToExistingObservation(
    location: Location,
    observationId: number,
): void {
    // can't use router 'cause the visible reload isn't wanted and onSameUrlNavigation='reload' is being used on observations
    location.replaceState(
        `/${baseCmd}/${
            matchUserId(location.path())[1]
        }/observation/${observationId}`,
        location.path().split('?')[1],
    );
}

export function replaceStateToExistingCommitment(
    location: Location,
    commitmentId: number,
): void {
    // can't use router 'cause the visible reload isn't wanted and onSameUrlNavigation='reload' is being used on commitments
    location.replaceState(
        `/${baseCmd}/${
            matchUserId(location.path())[1]
        }/commitments/${commitmentId}`,
        location.path().split('?')[1],
    );
}

export function replaceStateToExistingDraftCommitment(
    location: Location,
    draftId: string,
    approvalId: number,
    commitmentId: number,
): void {
    const userId = matchUserId(location.path())[1];
    let path =
        commitmentId != null
            ? `/${baseCmd}/${userId}/commitments/${commitmentId}/drafts/${draftId}`
            : `/${baseCmd}/${userId}/draftcommitments/${draftId}`;
    if (approvalId != null) {
        path = `${path}/approvals/${approvalId}`;
    }

    // can't use router 'cause the visible reload isn't wanted and onSameUrlNavigation='reload' is being used on commitments
    location.replaceState(path, location.path().split('?')[1]);
}

export function matchUserId(url: string): RegExpExecArray {
    return /\/coaching-detail\/(\d+)(?:\?|\/|$)/i.exec(url);
}

export function matchIsOnWorkArea(url: string): boolean {
    return /\/(observation|commitment|commitments|draftcommitments)(\?|\/|$)/i.test(
        url,
    );
}

export function createCoachingDetailCommands(opts: {
    userId: number;
    metricId: number;
}): [any[], { [key: string]: any }] {
    const cmds = ['/', baseCmd, opts.userId];
    const extras = { queryParams: { metricId: opts.metricId } };
    return [cmds, extras];
}

export function createCommitmentCommands(opts: {
    userId: number;
    draftId: string;
    approvalId: number;
    commitmentId: number;
}): [any[], { [key: string]: any }] {
    const cmds = ['/', baseCmd, opts.userId];
    if (opts.commitmentId != null) {
        cmds.push('commitments', opts.commitmentId);
    }
    if (opts.commitmentId != null && opts.draftId != null) {
        cmds.push('drafts', opts.draftId);
    }
    if (opts.commitmentId == null && opts.draftId != null) {
        cmds.push('draftcommitments', opts.draftId);
    }
    if (opts.approvalId != null) {
        cmds.push('approvals', opts.approvalId);
    }
    return [cmds, null];
}
